import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  signInWithGoogle,
  signOut as authSignOut,
  getIdToken,
  onAuthStateChange,
  type AuthUser,
} from '@/services/auth.service';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const loading = ref(true);
  const token = ref<string | null>(null);
  const initialized = ref(false);

  const isAuthenticated = computed(() => !!user.value);

  function setUser(newUser: AuthUser | null) {
    user.value = newUser;
  }

  function setToken(newToken: string | null) {
    token.value = newToken;
  }

  function setLoading(isLoading: boolean) {
    loading.value = isLoading;
  }

  async function signIn(): Promise<void> {
    loading.value = true;
    try {
      const authUser = await signInWithGoogle();
      user.value = authUser;
      token.value = await getIdToken();
    } finally {
      loading.value = false;
    }
  }

  async function signOut(): Promise<void> {
    loading.value = true;
    try {
      await authSignOut();
      user.value = null;
      token.value = null;
    } finally {
      loading.value = false;
    }
  }

  async function refreshToken(): Promise<string | null> {
    const newToken = await getIdToken();
    token.value = newToken;
    return newToken;
  }

  function initAuthListener(): void {
    if (initialized.value) return;

    onAuthStateChange(async (authUser) => {
      user.value = authUser;
      if (authUser) {
        token.value = await getIdToken();
      } else {
        token.value = null;
      }
      loading.value = false;
      initialized.value = true;
    });
  }

  return {
    user,
    loading,
    token,
    initialized,
    isAuthenticated,
    setUser,
    setToken,
    setLoading,
    signIn,
    signOut,
    refreshToken,
    initAuthListener,
  };
});
